<template>
<LoadError v-if="loadError && !isLoading" :message="loadError" @retry="initializeApp">
  <v-btn variant="text" class="ml-2" @click="logout">Return to sign in</v-btn>
</LoadError>
<template v-else>

  <div v-if="!isLoading">
    <v-app :theme="theme" v-if="token" app>
      <v-dialog v-model="isFirstLogin" v-if="!isMobile()">
        <v-card class="w-100 ma-auto pa-4" height="100vh">
          <v-card-title>Welcome to WildTrackPro!</v-card-title>
          <v-card-subtitle>Learn how to use our software before you get started.</v-card-subtitle>
          <v-card-text>
            <TutorialPage />
          </v-card-text>
          <v-card-actions>
            <v-btn @click="isFirstLogin = false">Close</v-btn>
            <v-btn @click="bypassTutorial()">Close and don't show this again</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-app-bar v-if="!isMobile() && hasPaid" color="white" elevation="3" class="modern-navbar">
        <v-app-bar-nav-icon variant="text" @click.stop="drawer = !drawer" class="nav-icon-btn">
          <img style="height:42px; width:42px; object-fit:contain; border-radius: 8px;" :src="Logo" />
        </v-app-bar-nav-icon>

        <v-app-bar-title style="cursor:pointer" @click="$router.push({ name: 'Home' })" class="marker modern-title">
          <span>WildTrackPro</span>
          <span v-if="currentPageName" class="page-breadcrumb">
            <v-icon size="18" class="breadcrumb-separator">mdi-chevron-right</v-icon>
            {{ currentPageName }}
          </span>
        </v-app-bar-title>

        <v-spacer></v-spacer>

        <v-tooltip text="Change group">
          <template v-slot:activator="{ props }">
            <span @click="changeGroupDialog = true" v-if="group" class="group-selector" v-bind="props">
              <v-chip label color="success" variant="outlined" prepend-icon="mdi-account-group"
                style="font-weight: 600; cursor:pointer; border-radius: 20px;" class="modern-chip">
                {{ group }}
              </v-chip>
            </span>
          </template>
        </v-tooltip>

        <v-dialog v-model="help">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" icon variant="text" class="help-btn" size="large">
              <v-icon color="success">mdi-help-circle</v-icon>
            </v-btn>
          </template>
          <HelpDialog key="help-dialog" @close="help = !help" :isMobile="isMobile()" />
        </v-dialog>
      </v-app-bar>

      <v-dialog v-model="changeGroupDialog" max-width="420px">
        <v-card class="pa-5 glass-card change-org-card" style="border-radius: 24px; overflow: visible;">
          <div class="text-center mb-4">
            <v-icon size="44" color="green" class="mb-2">mdi-account-switch</v-icon>
            <h2 class="change-org-title">Change Organisation</h2>
            <p class="change-org-subtitle">Switch to another group or organisation</p>
          </div>
          <v-combobox :menu-props="{ contentClass: 'overlay-menu', maxHeight: '40vh' }" v-model="chosenGroup"
            label="Organisation" :items="filteredGroups.sort()" class="modern-input compact-input mb-2"
            density="comfortable" color="green" variant="outlined" required />
          <div class="d-flex flex-column gap-2 mt-4">
            <v-tooltip text="Change group">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" :disabled="!chosenGroup" @click="selectGroup" color="success" class="change-org-btn"
                  block>Continue</v-btn>
              </template>
            </v-tooltip>
            <v-btn @click="changeGroupDialog = false" variant="text" class="change-org-cancel-btn" block>Cancel</v-btn>
          </div>
        </v-card>
      </v-dialog>

      <v-navigation-drawer v-model="drawer" temporary v-if="!isMobile() && hasPaid" location="left" width="280"
        class="modern-drawer">
        <!-- Header Section -->
        <div class="drawer-header user-header">
          <div class="d-flex align-center pa-4">
            <v-menu offset-y min-width="260" max-width="320" transition="scale-transition">
              <template v-slot:activator="{ props }">
                <div v-bind="props" class="user-avatar-box clickable-avatar">
                  <template v-if="userObject && userObject.mug_link && userObject.mug_link.startsWith('http') && !avatarError">
                    <img :src="getAvatarUrl(userObject.mug_link)" alt="User avatar" class="user-avatar-img" @error="avatarError = true" />
                  </template>
                  <template v-else>
                    <v-icon size="32" color="success">mdi-account</v-icon>
                  </template>
                </div>
              </template>
              <v-list class="user-menu-list enhanced-user-menu">
                <v-list-item @click="settingsDialog = true" class="user-menu-item">
                  <v-list-item-title> <v-icon start color="success" class="user-menu-icon">mdi-cog-outline</v-icon>
                    Settings</v-list-item-title>
                </v-list-item>
                <v-list-item @click="logout" class="user-menu-item">
                  <v-list-item-title><v-icon start color="error" class="user-menu-icon">mdi-logout</v-icon> Sign
                    Out</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <div class="ml-3">
              <v-tooltip location="bottom" open-delay="200">
                <template v-slot:activator="{ props }">
                  <h3 class="drawer-title user-drawer-title readable-username" v-bind="props"
                    :style="{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: '\'PT Sans\', Arial, sans-serif', fontWeight: 700, fontSize: '1.08rem', color: '#2e7d32', letterSpacing: '0.2px' }">
                    {{ userObject && userObject.username ? userObject.username : 'User' }}
                  </h3>
                </template>
                <span>
                  {{ userObject && userObject.username ? userObject.username : 'User' }}
                  <span v-if="userObject && userObject.username && group"> &nbsp;|&nbsp; </span>
                  <span v-if="group">{{ group }}</span>
                </span>
              </v-tooltip>
              <p class="drawer-subtitle clickable-group" @click="changeGroupDialog = true" style="cursor: pointer;">{{ group }}</p>
            </div>
          </div>
          <v-divider class="mb-2"></v-divider>
        </div>

        <!-- Navigation Items -->
        <v-list nav class="drawer-nav">
          <v-tooltip text="View our most popular services." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Home' })" prepend-icon="mdi-home" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Home' }" rounded="lg">
                <v-list-item-title>Home</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">OVERVIEW</v-list-subheader>
          <v-tooltip text="View your group's summary and key metrics." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Statistics' })" prepend-icon="mdi-view-dashboard-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Statistics' }" rounded="lg">
                <v-list-item-title>Dashboard</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">FIELDWORK</v-list-subheader>
          <v-tooltip text="View zones, transects, and overlays on an interactive map." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Map' })" prepend-icon="mdi-map-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Map' }" rounded="lg">
                <v-list-item-title>Map View</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View a list of outings for your group." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Reports' })" prepend-icon="mdi-clipboard-text-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Reports' }" rounded="lg">
                <v-list-item-title>Outings</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View all group members currently on-site." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'TrackGroupMembers' })" prepend-icon="mdi-crosshairs-gps" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'TrackGroupMembers' }" rounded="lg">
                <v-list-item-title>Live User Portal</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View and manage land management jobs." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Jobs' })" prepend-icon="mdi-clipboard-check-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Jobs' }" rounded="lg">
                <v-list-item-title>Jobs</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View and edit your sites, traps, and structures." location="end" v-if="isAdmin">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="siteSettingsDialog = true" prepend-icon="mdi-pine-tree" 
                class="nav-item" rounded="lg">
                <v-list-item-title>Site & Structure Data</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">RECORDS</v-list-subheader>
          <v-tooltip text="View or create reports and documents." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Reporting' })" prepend-icon="mdi-file-document-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Reporting' }" rounded="lg">
                <v-list-item-title>Reports</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View statistics and trends for your group." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Advanced' })" prepend-icon="mdi-chart-areaspline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Advanced' }" rounded="lg">
                <v-list-item-title>Statistics</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View wildlife management records for your group." location="end" v-if="cull_access">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Culls' })" prepend-icon="mdi-notebook-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Culls' }" rounded="lg">
                <v-list-item-title>Culls</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">SETTINGS</v-list-subheader>
          <v-tooltip text="View and edit information about your group." location="end" v-if="isAdmin || isInDemoGroup">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="groupSettingsDialog = true" prepend-icon="mdi-account-cog-outline" 
                class="nav-item" rounded="lg">
                <v-list-item-title>Group Settings</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
        </v-list>

        <!-- Footer Section -->
        <template v-slot:append>
          <div class="drawer-footer">
            <v-divider class="mb-3"></v-divider>
            <div class="pa-3">
              <v-btn @click="dialog = true" color="success" variant="outlined" block class="logout-btn"
                prepend-icon="mdi-ticket">
                Raise A Ticket
              </v-btn>
            </div>
          </div>
        </template>
      </v-navigation-drawer>

      <v-app-bar v-else color="white" elevation="2" class="modern-navbar mobile-navbar">
        <v-app-bar-nav-icon variant="text" @click.stop="drawer = !drawer" class="nav-icon-btn">
          <img style="height:32px; width:32px; object-fit:contain; border-radius: 6px;" :src="Logo" />
        </v-app-bar-nav-icon>
        <v-app-bar-title class="marker modern-title mobile-title">
          <span>WildTrackPro</span>
          <span v-if="currentPageName" class="page-breadcrumb mobile-breadcrumb">
            <v-icon size="16" class="breadcrumb-separator">mdi-chevron-right</v-icon>
            {{ currentPageName }}
          </span>
        </v-app-bar-title>
      </v-app-bar>

      <v-navigation-drawer v-model="drawer" temporary v-if="isMobile() && hasPaid" width="280"
        class="modern-drawer mobile-drawer">
        <!-- Header Section (copied from desktop, adapted for mobile) -->
        <div class="drawer-header user-header">
          <div class="d-flex align-center pa-4">
            <v-menu offset-y min-width="260" max-width="320" transition="scale-transition">
              <template v-slot:activator="{ props }">
                <div v-bind="props" class="user-avatar-box clickable-avatar">
                  <template v-if="userObject && userObject.mug_link && userObject.mug_link.startsWith('http') && !avatarError">
                    <img :src="getAvatarUrl(userObject.mug_link)" alt="User avatar" class="user-avatar-img" @error="avatarError = true" />
                  </template>
                  <template v-else>
                    <v-icon size="32" color="success">mdi-account</v-icon>
                  </template>
                </div>
              </template>
              <v-list class="user-menu-list enhanced-user-menu">
                <v-list-item @click="settingsDialog = true" class="user-menu-item">
                  <v-list-item-title> <v-icon start color="success" class="user-menu-icon">mdi-cog-outline</v-icon>
                    Settings</v-list-item-title>
                </v-list-item>
                <v-list-item @click="logout" class="user-menu-item">
                  <v-list-item-title><v-icon start color="error" class="user-menu-icon">mdi-logout</v-icon> Sign
                    Out</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <div class="ml-3">
              <v-tooltip location="bottom" open-delay="200">
                <template v-slot:activator="{ props }">
                  <h3 class="drawer-title user-drawer-title readable-username" v-bind="props"
                    :style="{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: '\'PT Sans\', Arial, sans-serif', fontWeight: 700, fontSize: '1.08rem', color: '#2e7d32', letterSpacing: '0.2px' }">
                    {{ userObject && userObject.username ? userObject.username : 'User' }}
                  </h3>
                </template>
                <span>
                  {{ userObject && userObject.username ? userObject.username : 'User' }}
                  <span v-if="userObject && userObject.username && group"> &nbsp;|&nbsp; </span>
                  <span v-if="group">{{ group }}</span>
                </span>
              </v-tooltip>
              <p class="drawer-subtitle clickable-group" @click="changeGroupDialog = true" style="cursor: pointer;">{{ group }}</p>
            </div>
          </div>
          <v-divider class="mb-2"></v-divider>
        </div>

        <!-- Navigation Items -->
        <v-list nav class="drawer-nav">
          <v-tooltip text="View our most popular services." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Home' })" prepend-icon="mdi-home" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Home' }" rounded="lg">
                <v-list-item-title>Home</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">OVERVIEW</v-list-subheader>
          <v-tooltip text="View your group's summary and key metrics." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Statistics' })" prepend-icon="mdi-view-dashboard-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Statistics' }" rounded="lg">
                <v-list-item-title>Dashboard</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">FIELDWORK</v-list-subheader>
          <v-tooltip text="View zones, transects, and overlays on an interactive map." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Map' })" prepend-icon="mdi-map-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Map' }" rounded="lg">
                <v-list-item-title>Map View</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View a list of outings for your group." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Reports' })" prepend-icon="mdi-clipboard-text-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Reports' }" rounded="lg">
                <v-list-item-title>Outings</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View all group members currently on-site." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'TrackGroupMembers' })" prepend-icon="mdi-crosshairs-gps" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'TrackGroupMembers' }" rounded="lg">
                <v-list-item-title>Live User Portal</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View and manage land management jobs." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Jobs' })" prepend-icon="mdi-clipboard-check-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Jobs' }" rounded="lg">
                <v-list-item-title>Jobs</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View and edit your sites, traps, and structures." location="end" v-if="isAdmin">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="siteSettingsDialog = true" prepend-icon="mdi-pine-tree" 
                class="nav-item" rounded="lg">
                <v-list-item-title>Site & Structure Data</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">RECORDS</v-list-subheader>
          <v-tooltip text="View statistics and trends for your group." location="end">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Advanced' })" prepend-icon="mdi-chart-areaspline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Advanced' }" rounded="lg">
                <v-list-item-title>Statistics</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-tooltip text="View wildlife management records for your group." location="end" v-if="cull_access">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="$router.push({ name: 'Culls' })" prepend-icon="mdi-notebook-outline" 
                class="nav-item" :class="{ 'nav-item-active': $route.name === 'Culls' }" rounded="lg">
                <v-list-item-title>Culls</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-list-subheader class="nav-subheader">SETTINGS</v-list-subheader>
          <v-tooltip text="View and edit information about your group." location="end" v-if="isAdmin || isInDemoGroup">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" @click="groupSettingsDialog = true" prepend-icon="mdi-account-cog-outline" 
                class="nav-item" rounded="lg" :isDemoUser="isInDemoGroup">
                <v-list-item-title>Group Settings</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>

          <v-dialog v-model="dialog" :width="!isMobile() ? '50vw' : '100vw'">
            <Request @success="dialog = false" @close="dialog = false" />
          </v-dialog>

          <v-dialog v-model="help">
            <template v-slot:activator="{ props }">
              <v-tooltip text="View help resources for WildTrackPro" location="end">
                <template v-slot:activator="{ props: tooltipProps }">
                  <v-list-item v-bind="{ ...props, ...tooltipProps }" prepend-icon="mdi-help-circle" class="nav-item" rounded="lg">
                    <v-list-item-title>Help</v-list-item-title>
                  </v-list-item>
                </template>
              </v-tooltip>
            </template>
            <HelpDialog key="help-dialog" @close="help = !help" :isMobile="isMobile()" />
          </v-dialog>
        </v-list>

        <!-- Footer Section -->
        <template v-slot:append>
          <div class="drawer-footer">
            <v-divider class="mb-3"></v-divider>
            <div class="pa-3">
              <v-btn @click="dialog = true" color="success" variant="outlined" block class="logout-btn"
                prepend-icon="mdi-ticket">
                Raise A Ticket
              </v-btn>
            </div>
          </div>
        </template>
      </v-navigation-drawer>
      <v-card variant="tonal" v-if="ongoingAlerts.length > 0 && showAlerts" class="pa-6">
        <h1 class="my-2">Alert<span v-if="ongoingAlerts.length > 1">s ({{ ongoingAlerts.length }})</span></h1>
        <div class="mb-1" v-for="r, i in ongoingAlerts" :key="i"><v-chip label class="mr-4"
            :class="{ 'bg-red': r.handledby.length === 0, 'bg-orange': r.handledby.length >= 2, 'bg-green': r.type === 'notification', }"></v-chip>{{
              r.context }} (<span v-if="r.handledby.length > 0">Being handled by {{ r.handledby }}</span><span
            v-else>Unhandled</span>) <v-chip class="ml-2" variant="outlined" label>{{ r.locationref }}</v-chip><br
            v-if="isMobile()" />
          <v-dialog v-if="r.type != 'notification'"><template v-slot:activator="{ props }"><v-btn variant="plain"
                v-bind="props">I'm handling this</v-btn></template>
            <Resolve :date="r.date" :context="r.context" type="handle" :rownum="i" :isMobile="isMobile()" />
          </v-dialog>
          <v-dialog v-if="r.type != 'notification'"><template v-slot:activator="{ props }"><v-btn variant="plain"
                v-bind="props">I've resolved this</v-btn></template>
            <Resolve :date="r.date" :context="r.context" type="resolve" :rownum="i" :isMobile="isMobile()" />
          </v-dialog>
        </div>
        <v-btn class="mt-4" variant="tonal" @click="showAlerts = false">Dismiss</v-btn>
      </v-card>

      <v-card @click="certInfo ? certDialog = true : certOpen = false" elevation="0"
        v-if="(certLoading || certInfo || certError) && certOpen"
        :class="{ 'bg-green-lighten-3': certInfo && !certLoading, 'bg-orange-lighten-3': certLoading && !certInfo, 'bg-red-lighten-3': certError && !certLoading }"
        class="pa-5" style="top:6%">
        <v-icon v-if="certInfo" class="mr-6">mdi-check</v-icon> <span v-if="certInfo">Click to view document ({{
          certMeta.name }})</span>
        <v-icon v-if="certError" class="mr-6">mdi-alpha-x-circle</v-icon> <span v-if="certError">{{ certError
        }}</span>
        <span v-if="certLoading"><v-btn variant="plain" elevation="0" :loading="true"
            class="mr-4"></v-btn>Loading...</span>
        <v-dialog v-model="certDialog" max-width="600px">
          <v-card class="pa-4">
            <!-- Dialog Title with Close Button -->
            <v-card-title>
              <span>{{ certMeta.name }} Response</span>
              <v-spacer></v-spacer>
              <!-- "X" Close Button on the right -->
              <v-btn icon @click="certDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <!-- Dialog Content with Scrolling -->
            <v-card-text style="overflow: auto;">
              This document has been completed. Please navigate to the <a href="" @click="$router.push({name: 'Reporting'})">Reporting</a> page and your new document should be at the top of the list.
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-card>
      <!-- 
      Renders all routed content from the /views directory 
    -->
      <v-main id="main_slot">
        <v-progress-linear v-if="navigationState.loading" indeterminate color="green" aria-label="Loading page" />
        <router-view @openDrawer="drawer = true" :isDemoUser="isInDemoGroup" :isSuperuser="isSuperuser"
          :advancedAccess="advancedAccess" :cullAccess="cull_access" :isMobile="isMobile()" :isAdmin="isAdmin"
          :hasPaid="hasPaid" :group="group" :alocs="userLocations" :spreadsheetId="spreadsheetId"
          :isDocGenerating="isDocumentGenerating" @cloudFunction="runCloudFunction" />
      </v-main>

    </v-app>

    <!--
    Renders the login screen if there is no token bound. A different app is used temporarily until the navbar is loaded into a component.
  -->
    <v-app v-else>
      <Login @success="setToken" @superuser="setSuperuser" :isMobile="isMobile()" />
    </v-app>
  </div>
  <div v-else>
    <Loading fullscreen label="Loading WildTrackPro?" />
  </div>

  <v-dialog v-model="settingsDialog" fullscreen :scrim="false" transition="dialog-bottom-transition">
    <UserSettings @close="settingsDialog = false" :isMobile="isMobile()" />
  </v-dialog>

  <v-dialog v-model="groupSettingsDialog" fullscreen :scrim="false" transition="dialog-bottom-transition">
    <GroupSettings @close="groupSettingsDialog = false" :isMobile="isMobile" :group="group" :isDemoUser="isInDemoGroup"
      :isAdmin="isAdmin" />
  </v-dialog>

  <v-dialog v-model="siteSettingsDialog" fullscreen :scrim="false" transition="dialog-bottom-transition">
    <SiteSettings @close="siteSettingsDialog = false" :isMobile="isMobile" />
  </v-dialog>

  <v-dialog v-model="dialog" width="50vw">
    <Request @success="dialog = false" @close="dialog = false" />
  </v-dialog>

  <!-- Additional dialogs for mobile functionality -->
  <v-dialog v-model="sr_diag" class="w-100">
    <Info sheet_name="structures_reporting" @close="sr_diag = false" :isMobile="isMobile" />
  </v-dialog>

  <v-dialog v-model="si_diag" class="w-100">
    <Info sheet_name="structures_inspection" @close="si_diag = false" :isMobile="isMobile" />
  </v-dialog>

</template>
<AppFeedback />
</template>



<style>
@import url('https://fonts.googleapis.com/css2?family=PT+Sans:wght@400;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap');

#main_slot {
  font-family: 'PT Sans', sans-serif;
}

.marker {
  font-family: 'Permanent Marker', cursive;
}

/* Modern Navigation Styles */
.modern-navbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
}

.modern-title {
  color: #2e7d32 !important;
  font-weight: 600;
  font-size: 1.3rem !important;
  letter-spacing: 0.5px;
}

/* Page Breadcrumb Styles */
.page-breadcrumb {
  font-family: 'PT Sans', Arial, sans-serif;
  font-weight: 500;
  font-size: 1rem !important;
  color: #558b2f !important;
  margin-left: 8px;
  opacity: 0.9;
  display: inline-flex;
  align-items: center;
}

.breadcrumb-separator {
  color: #81c784 !important;
  margin: 0 4px;
  opacity: 0.7;
}

.mobile-breadcrumb {
  font-size: 0.85rem !important;
  margin-left: 4px;
}

.mobile-breadcrumb .breadcrumb-separator {
  margin: 0 2px;
}

.mobile-title {
  font-size: 1.1rem !important;
}

.nav-icon-btn {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.nav-icon-btn:hover {
  background-color: rgba(46, 125, 50, 0.08);
}

.group-selector {
  margin-right: 16px;
}

.modern-chip {
  height: 40px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.modern-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.help-btn {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.help-btn:hover {
  background-color: rgba(76, 175, 80, 0.08);
}

/* Modern Drawer Styles */
.modern-drawer {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

.drawer-header {
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-bottom: 1px solid rgba(76, 175, 80, 0.1);
}

.drawer-logo {
  height: 40px;
  width: 40px;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.drawer-title {
  font-family: 'Permanent Marker', cursive;
  color: #2e7d32;
  font-size: 1.1rem;
  margin: 0;
  font-weight: 600;
}

.drawer-subtitle {
  color: #558b2f;
  font-size: 0.85rem;
  margin: 0;
  font-weight: 500;
  opacity: 0.9;
}

.drawer-nav {
  padding: 8px 12px;
}

.nav-subheader {
  color: #2e7d32 !important;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.8px;
  margin: 20px 0 8px 0;
  opacity: 0.8;
}

.nav-item {
  margin: 4px 0;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  color: #424242;
}

.nav-item:hover {
  background-color: rgba(76, 175, 80, 0.08) !important;
  transform: translateX(4px);
}

.nav-item .v-list-item-title {
  font-weight: 500;
  font-size: 0.9rem;
}

.nav-item .v-icon {
  color: #558b2f;
  opacity: 0.8;
}

.nav-item:hover .v-icon {
  color: #2e7d32;
  opacity: 1;
}

.nav-item-active {
  background-color: rgba(76, 175, 80, 0.12) !important;
  border-left: 4px solid #4caf50;
  transform: translateX(2px);
}

.nav-item-active .v-list-item-title {
  color: #2e7d32 !important;
  font-weight: 600 !important;
}

.nav-item-active .v-icon {
  color: #2e7d32 !important;
  opacity: 1 !important;
}

.drawer-footer {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 1px solid rgba(76, 175, 80, 0.1);
}

.logout-btn {
  border-radius: 12px;
  font-weight: 600;
  height: 44px;
  transition: all 0.3s ease;
  border: 2px solid #4caf50;
}

.logout-btn:hover {
  background-color: #4caf50;
  color: white !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.3);
}
.logout-btn:hover .v-btn__content,
.logout-btn:hover .v-icon,
.logout-btn:hover .v-list-item-title {
  color: white !important;
}

/* Mobile specific styles */
.mobile-navbar {
  padding: 0 8px;
}

.mobile-drawer .drawer-header {
  padding: 16px;
}

.mobile-drawer .drawer-title {
  font-size: 1rem;
}

.mobile-drawer .drawer-subtitle {
  font-size: 0.8rem;
}

.mobile-drawer .nav-item {
  margin: 6px 0;
}

.mobile-drawer .nav-item .v-list-item-title {
  font-size: 0.95rem;
}

/* Enhanced transitions */
.v-navigation-drawer {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.v-app-bar {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .modern-title {
    font-size: 1rem !important;
  }

  .page-breadcrumb {
    font-size: 0.8rem !important;
    margin-left: 4px;
  }

  .breadcrumb-separator {
    margin: 0 2px !important;
  }

  .nav-icon-btn img {
    height: 28px !important;
    width: 28px !important;
  }

  .drawer-logo {
    height: 36px;
    width: 36px;
  }
}
/* User Drawer Header Styles */
.user-header {
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-bottom: 1px solid rgba(76, 175, 80, 0.1);
}

/* Rounded box avatar container */
.user-avatar-box {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #fff;
  border: 2px solid #4caf50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.12);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.user-avatar-box:hover {
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.22);
  transform: translateY(-1px);
  border-color: #388e3c;
}

.user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Legacy avatar styles for backwards compatibility */
.user-avatar {
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.08);
  background: #fff;
  transition: box-shadow 0.2s;
}

.user-avatar.clickable-avatar:hover {
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.18);
}
</style>
.user-drawer-title {
font-family: 'PT Sans', Arial, sans-serif !important;
color: #2e7d32 !important;
font-size: 1.08rem !important;
margin: 0;
font-weight: 700 !important;
letter-spacing: 0.2px;
max-width: 160px;
overflow: hidden;
text-overflow: ellipsis;
white-space: nowrap;
cursor: pointer;
transition: color 0.2s;
}
.readable-username:hover {
color: #388e3c !important;
}
.user-menu-list {
min-width: 160px;
padding: 0;
}
.enhanced-user-menu {
background: #fff;
border-radius: 14px;
box-shadow: 0 6px 24px rgba(76, 175, 80, 0.10), 0 1.5px 4px rgba(76, 175, 80, 0.08);
padding: 6px 0;
min-width: 260px;
max-width: 320px;
}
.user-menu-item {
cursor: pointer;
font-size: 1rem;
font-weight: 500;
color: #2e7d32;
border-radius: 8px;
transition: background 0.18s, color 0.18s, box-shadow 0.18s;
display: flex;
align-items: center;
padding: 8px 18px 8px 14px;
margin: 0 4px;
position: relative;
}
.user-menu-item:hover {
background: linear-gradient(90deg, #e8f5e9 60%, #f1f8e9 100%);
color: #388e3c !important;
box-shadow: 0 2px 8px rgba(76, 175, 80, 0.10);
}
.user-menu-item:active {
background: #c8e6c9;
}
.user-menu-icon {
margin-right: 12px;
opacity: 0.85;
transition: color 0.18s;
}
.user-menu-list .v-list-item {
cursor: pointer;
font-size: 1rem;
font-weight: 500;
color: #2e7d32;
border-radius: 8px;
transition: background 0.2s;
}
.user-menu-list .v-list-item:hover {
background: #e8f5e9;
}

/* Change Organisation Dialog Styles */
.change-org-card {
backdrop-filter: blur(20px) !important;
-webkit-backdrop-filter: blur(20px) !important;
background: rgba(255, 255, 255, 0.95) !important;
border: 1px solid rgba(255, 255, 255, 0.3) !important;
box-shadow:
0 20px 40px rgba(0, 0, 0, 0.18),
0 10px 20px rgba(0, 0, 0, 0.08),
inset 0 1px 0 rgba(255, 255, 255, 0.4) !important;
}
.change-org-title {
font-size: 1.5rem;
font-weight: 700;
background: linear-gradient(135deg, #4CAF50, #2E7D32);
-webkit-background-clip: text;
-webkit-text-fill-color: transparent;
background-clip: text;
margin: 8px 0 0 0;
text-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}
.change-org-subtitle {
color: rgba(0, 0, 0, 0.6);
font-size: 1.05rem;
font-weight: 500;
margin: 0 0 8px 0;
}
.change-org-btn {
background: linear-gradient(135deg, #4CAF50, #2E7D32) !important;
color: white !important;
border-radius: 12px !important;
font-weight: 600;
font-size: 1.1rem;
text-transform: none;
padding: 12px 0 !important;
box-shadow:
0 8px 16px rgba(76, 175, 80, 0.18),
0 4px 8px rgba(76, 175, 80, 0.12) !important;
line-height: 1.5 !important;
min-height: 44px !important;
display: flex;
align-items: center;
justify-content: center;
}
.change-org-btn:active {
transform: translateY(0);
}
.change-org-btn:disabled {
opacity: 0.7;
}
.change-org-cancel-btn {
color: #2e7d32 !important;
font-weight: 500;
border-radius: 10px !important;
text-transform: none;
padding: 10px 0 !important;
}
.change-org-cancel-btn:hover {
background: rgba(76, 175, 80, 0.08) !important;
color: #388e3c !important;
}

<script>
import { parseArray, deepCopyWithoutCircularRefs } from '@/services/data';

import { navigationState } from '@/router';

import LoadingComponent from '@/components/LoadingComponent.vue';

import { defineAsyncComponent } from 'vue';
import AppFeedback from '@/components/AppFeedback.vue';
import { loadData, reportError } from '@/services/errors';

import creds from '@/whatsapp-tracking-361713-97a0a2bb9e20.json';
import { GoogleSpreadsheet } from '@/services/spreadsheet';
import Login from './views/Login.vue';
const HelpDialog = defineAsyncComponent({
  loader: () => import('./components/HelpDialog.vue'),
  loadingComponent: LoadingComponent,
  delay: 150,
});
const Request = defineAsyncComponent({
  loader: () => import('./components/RequestComponent.vue'),
  loadingComponent: LoadingComponent,
  delay: 150,
});
const Resolve = defineAsyncComponent({
  loader: () => import('./components/ResolveAlert.vue'),
  loadingComponent: LoadingComponent,
  delay: 150,
});
const GroupSettings = defineAsyncComponent({
  loader: () => import('@/components/GroupSettings.vue'),
  loadingComponent: LoadingComponent,
  delay: 150,
});
const UserSettings = defineAsyncComponent({
  loader: () => import('@/components/UserSettings.vue'),
  loadingComponent: LoadingComponent,
  delay: 150,
});
const TutorialPage = defineAsyncComponent({
  loader: () => import('@/components/Tutorial.vue'),
  loadingComponent: LoadingComponent,
  delay: 150,
});
const SiteSettings = defineAsyncComponent({
  loader: () => import('@/components/SiteSettings.vue'),
  loadingComponent: LoadingComponent,
  delay: 150,
});
import Logo from '@/assets/logo-wtp.png';
import axios from '@/services/http';
export default {
  name: 'App',
  components: {
    AppFeedback,
    Login,
    HelpDialog,
    Request,
    Resolve,
    GroupSettings,
    UserSettings,
    TutorialPage,
    SiteSettings,
  },
  setup: () => {
    return { Logo, navigationState };
  },
  data() {
    return {
      loadError: '',
      token: null,
      username: null,
      permissions: null,
      first_name: null,
      isGroupAdmin: false,
      isInDemoGroup: false,
      theme: 'light',
      help: false,
      drawer: false,
      group: null,
      userObject: null,
      phone: null,
      cull_access: false,
      user_info: null,
      dialog: false,
      changeGroupDialog: false,
      isAdmin: false,
      chosenGroup: null,
      ongoingAlerts: [],
      showAlerts: true,
      allGroups: null,
      userGroups: null,
      filteredGroups: null,
      hasPaid: null,
      isLoading: false,
      rowNum: null,
      settingsDialog: false,
      groupSettingsDialog: false,
      siteSettingsDialog: false,
      sr_diag: false,
      si_diag: false,
      userLocations: null,
      spreadsheetId: null,
      certLoading: false,
      certInfo: null,
      certError: null,
      certDialog: false,
      certMeta: null,
      certOpen: true,
      advancedAccess: false,
      isFirstLogin: false,
      isDocumentGenerating: false,
      isSuperuser: false,
      apphelpdiag: false,
      whatsapphelpdiag: false,
      avatarError: false,
    };
  },
  async created() {
    await this.initializeApp();
  },
  computed: {
    currentPageName() {
      const routeNameMap = {
        Home: null, // Don't show anything for home page
        Map: 'Map View',
        Statistics: 'Dashboard',
        Reports: 'Outings',
        TrackGroupMembers: 'Live User Portal',
        Reporting: 'Reports',
        Advanced: 'Stats',
        Culls: 'Culls Database',
        Sites: 'Sites',
        CurrentOuting: 'Current Outing',
        Bookings: 'Bookings',
        CullsVsPlans: 'Culls vs Plans',
        Jobs: 'Jobs',
      };
      return routeNameMap[this.$route.name] || null;
    },
  },
  methods: {
    async initializeApp() {
      return loadData(this, async () => {
        if (!localStorage.getItem('username')) return;
        // Spreadsheet configuration
        const spreadsheets = {
          userDoc: { id: '1_QB8JSWR_AINfhpKFLp95NjLP-uFHWd9j1dbNnk2EAk', sheet: 'user_db' },
          groupDoc: { id: '1Q_YojT0KtC61QCLIZfTJdonXHG1EWwYMlm5fz9PACA4', sheet: 'orgs_db' },
          alertDoc: { id: '1WVxAscDAWoZk6GThin-YQWvdr2QSR88mwk6zYSqmTfo', sheet: 'alerts_db' },
        };

        const fetchSheetRows = async (docId, sheetName) => {
          const doc = new GoogleSpreadsheet(docId);
          await doc.useServiceAccountAuth({
            client_email: creds.client_email,
            private_key: creds.private_key,
          });
          await doc.loadInfo();
          const sheet = doc.sheetsByTitle[sheetName];
          return await sheet.getRows();
        };

        // Fetch user data
        const userRows = await fetchSheetRows(spreadsheets.userDoc.id, spreadsheets.userDoc.sheet);
        const currentUser = userRows.find((r) => r.username === localStorage.getItem('username'));
        if (!currentUser) {
          this.token = null;
          return;
        }
        this.userObject = currentUser;
        // Set user variables
        this.token = currentUser.password;
        this.first_name = currentUser.first_name;
        this.group = localStorage.getItem('group');
        this.phone = currentUser.phone;
        this.user_info = this.deepCopyWithoutCircularRefs(currentUser);
        this.cull_access = currentUser.cull_access === 'TRUE';
        this.advancedAccess = parseArray(currentUser.app_permissions || '[]').includes('ADMIN');
        this.hasPaid = currentUser.paid === 'TRUE';
        this.userLocations = currentUser.site_permissions;

        // Fetch group data
        const groupRows = await fetchSheetRows(spreadsheets.groupDoc.id, spreadsheets.groupDoc.sheet);
        const currentGroup = groupRows.find((r) => r.group_name === this.group);

        if (!currentGroup) throw new Error('Organisation unavailable');
        // Set group variables
        this.isInDemoGroup = currentGroup.group_name === 'WildTrackPro Demo Group';
        this.allGroups = this.deepCopyWithoutCircularRefs(groupRows).map((r) => r.group_name);
        this.userGroups = currentUser.groups.split(',');
        this.filteredGroups = this.userGroups[0] === 'ADMIN' ? this.allGroups : this.userGroups;
        this.spreadsheetId = currentGroup.group_db_id;
        localStorage.setItem('WildTrackProGroupFolderID', currentGroup.group_folder);
        localStorage.setItem('WTPGT', JSON.stringify(this.deepCopyWithoutCircularRefs(currentGroup)));
        this.isAdmin = currentGroup.admin_array.includes(localStorage.getItem('username'));
        if (currentUser.groups.split(',')[0] === 'ADMIN') {
          this.isAdmin = true;
        }

        // Track user login
        const currentUserIndex = userRows.findIndex((r) => r.username === localStorage.getItem('username'));
        this.rowNum = currentUserIndex;
        const userRow = userRows[this.rowNum];
        const lastLoginDifference = Math.abs(userRow.last_login - userRow.created_at) / (1000 * 60 * 60 * 24);
        this.isFirstLogin = lastLoginDifference <= 30 && currentUser.bypass_tutorial === 'FALSE';

        userRow.last_login = Date.now();
        try {
          await userRow.save();
        } catch (error) {
          reportError(error);
        }

        // Save localStorage data
        localStorage.setItem('first_name', this.first_name);
        localStorage.setItem('permissions', currentUser.site_permissions);
        localStorage.setItem('phone', currentUser.phone);
        localStorage.setItem('groupSpreadsheetIdWildTrackPro', this.spreadsheetId);

        // Fetch alerts
        try {
          const alertRows = await fetchSheetRows(spreadsheets.alertDoc.id, spreadsheets.alertDoc.sheet);
          this.ongoingAlerts = alertRows.filter(
            (r) => r.type === 'alert' || r.notification_groups.includes(this.group)
          );
        } catch (e) {
          reportError(e);
        }
      });
    },

    setSuperuser(resp) {
      if (resp) {
        this.isAdmin = true;
      }
    },
    selectGroup() {
      localStorage.setItem('group', this.chosenGroup);
      window.location.reload();
    },
    setToken(resp) {
      // Sets environment variables into localstorage for use by other components.
      // Can be changed to pass down data into router view.
      if (resp[2] === 'WildTrackPro Demo Group') {
        this.isInDemoGroup = true;
      }
      this.username = resp[1];
      localStorage.setItem('username', this.username);
      localStorage.setItem('group', resp[2]);
      window.location.reload();
    },
    async runCloudFunction(data) {
      this.isDocumentGenerating = true;
      this.certOpen = true;
      this.certMeta = data;
      this.certLoading = true;
      this.certInfo = null;
      this.certError = null;
      try {
        const response = await axios({
          method: 'post', // or 'post', 'put', etc, based on your cloud function
          url: data.function_url,
          data: data,
        });
        this.certInfo = response.data;
        this.certLoading = false;
        this.isDocumentGenerating = false;
      } catch (error) {
        this.certError = error;
        reportError(error);
        alert(this.certError);
        this.certLoading = false;
        this.isDocumentGenerating = false;
      }
    },
    deepCopyWithoutCircularRefs,
    async bypassTutorial() {
      const userdoc = new GoogleSpreadsheet('1_QB8JSWR_AINfhpKFLp95NjLP-uFHWd9j1dbNnk2EAk');
      await userdoc.useServiceAccountAuth({
        client_email: creds.client_email,
        private_key: creds.private_key,
      });
      await userdoc.loadInfo();
      const usersheet = userdoc.sheetsByTitle['user_db'];
      const userrows = await usersheet.getRows();

      let user = userrows.filter((r) => {
        return r.username === localStorage.getItem('username');
      })[0];

      user.bypass_tutorial = 'TRUE';
      await user.save();
      this.isFirstLogin = false;
    },
    logout() {
      this.$router.push({ name: 'Home' });
      this.loadError = '';
      this.token = null;
      localStorage.removeItem('groups');
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('permissions');
      localStorage.removeItem('first_name');
      localStorage.removeItem('phone');
      localStorage.removeItem('cull_access');
      localStorage.removeItem('ui');
      localStorage.removeItem('group');
      localStorage.removeItem('paid');
      localStorage.clear();
    },
    isMobile() {
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return true;
      } else {
        return false;
      }
    },
    getAvatarUrl(url) {
      // Convert Google Drive URLs to a more reliable format
      if (!url) return null;

      // Handle lh3.googleusercontent.com/d/ format
      // e.g., https://lh3.googleusercontent.com/d/FILE_ID=s1000?authuser=0
      const lh3Match = url.match(/lh3\.googleusercontent\.com\/d\/([^=?/]+)/);
      if (lh3Match) {
        const fileId = lh3Match[1];
        // Use the Google Drive thumbnail API which is more reliable
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
      }

      // Handle drive.google.com/file/d/ format
      const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
      if (driveMatch) {
        const fileId = driveMatch[1];
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
      }

      // Handle drive.google.com/open?id= format
      const openMatch = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
      if (openMatch) {
        const fileId = openMatch[1];
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
      }

      // Return original URL if no conversion needed
      return url;
    },
  },
};
</script>
